import { Checkbox } from "@mui/material";
import { MRT_ColumnDef, MRT_TableOptions, useMaterialReactTable } from "material-react-table";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useTableSettings } from "@/hooks/use-table-settings";
import CreateDomainTableControl from "@/module/rooms-management-module/components/create-domain-table-control";
import { SysDomainRegularDto } from "@/shared/api";

const useDomainsListTable = (
  {
    domains,
    refetchDomains,
  }: {
    domains: SysDomainRegularDto[];
    refetchDomains: () => void;
  },
  tableOptions?: Omit<MRT_TableOptions<SysDomainRegularDto>, "columns" | "data">
) => {
  const { t } = useTranslation();

  const columns = useMemo<MRT_ColumnDef<SysDomainRegularDto>[]>(
    () => [
      {
        id: "name",
        header: t("name"),
        accessorFn: (row) => row.name,
      },
      {
        id: "is_room",
        header: t("isRoom"),
        accessorFn: (row) => (row.is_room ? "true" : "false"),
        Cell: ({ cell }) => <Checkbox checked={cell.getValue() === "true"} disabled />,
      },
    ],
    [t]
  );

  const { defaultProps, state } = useTableSettings<SysDomainRegularDto>("domainsList");

  const table = useMaterialReactTable({
    data: domains,
    columns: columns,
    getRowId: (row) => String(row.name),
    ...defaultProps,
    initialState: {
      density: "compact",
      ...tableOptions?.initialState,
    },
    enableStickyHeader: true,
    renderTopToolbarCustomActions: () => {
      return (
        <div>
          <CreateDomainTableControl onSuccess={() => refetchDomains()} />
        </div>
      );
    },
    ...tableOptions,
    muiTablePaperProps: {
      sx: {
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        width: "100%",
      },
    },
    muiTableContainerProps: {
      sx: {
        flex: "1 1 auto",
        maxHeight: "none",
        minHeight: 0,
        overflow: "auto",
      },
    },
    state: {
      ...state,
      ...tableOptions?.state,
    },
  });

  const pageIndex = table.getState().pagination.pageIndex;

  useEffect(() => {
    table.refs.tableContainerRef.current?.scrollTo({ top: 0 });
  }, [pageIndex, table]);

  return table;
};

export default useDomainsListTable;
